import React, { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Check } from "lucide-react";
import { api } from "@/lib/api";

export default function BucketList({ items, setItems }) {
  const [title, setTitle] = useState("");

  const add = async () => {
    if (!title.trim()) return;
    const { data } = await api.post("/bucket-list", { title });
    setItems((x) => [data, ...x]);
    setTitle("");
  };

  const toggle = async (item) => {
    const { data } = await api.patch(`/bucket-list/${item.id}`, { done: !item.done });
    setItems((x) => x.map((i) => (i.id === item.id ? data : i)));
  };

  const remove = async (id) => {
    await api.delete(`/bucket-list/${id}`);
    setItems((x) => x.filter((i) => i.id !== id));
  };

  const done = items.filter((i) => i.done).length;

  return (
    <section id="dreams" className="max-w-6xl mx-auto px-6 py-16">
      <div className="mb-8">
        <p className="font-hand text-2xl text-[#D95F59]">things to do, together</p>
        <h2 className="font-display text-4xl tracking-tight">Our Dream List</h2>
        <p className="text-[#756D6A] mt-1">
          {done} of {items.length} adventures unlocked
        </p>
      </div>

      <div className="flex gap-3 mb-6 max-w-xl">
        <input
          data-testid="bucket-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add a dream date or adventure…"
          className="flex-1 rounded-full border border-[#EADEDA] px-5 py-3 bg-white outline-none focus:border-[#D95F59]"
        />
        <button
          data-testid="add-bucket-btn"
          onClick={add}
          className="rounded-full bg-[#D95F59] text-white px-5 hover:bg-[#C75D53] hover:scale-105 transition-all"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((item, i) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: (i % 2) * 0.05 }}
            data-testid="bucket-item"
            className="card-paper rounded-2xl p-5 flex items-center gap-4 group"
          >
            <button
              data-testid="toggle-bucket-btn"
              onClick={() => toggle(item)}
              className={`shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${
                item.done ? "bg-[#8C9A86] border-[#8C9A86]" : "border-[#EADEDA] hover:border-[#D95F59]"
              }`}
            >
              {item.done && <Check className="w-4 h-4 text-white" />}
            </button>
            <span
              className={`flex-1 text-lg ${
                item.done ? "line-through text-[#756D6A]" : "text-[#2A2726]"
              }`}
            >
              {item.title}
            </span>
            <button
              data-testid="delete-bucket-btn"
              onClick={() => remove(item.id)}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 className="w-4 h-4 text-[#D95F59]" />
            </button>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
